import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Callout, Card, GameKindTag, Heading, Text } from '../ui/index.js';
import type { GameKind } from '@goodbuild/shared';
import { api } from '../api/client.js';
import { keys, useAccess } from '../api/hooks.js';
import {
  ANDROID_PACKAGE_RULE,
  KindPicker,
  SLUG_RULE,
  SteamDescriptionField,
  StoreUrlFields,
  slugify,
  storeProblems,
  useAdminGames,
  type AdminGame,
  type StoreUrls,
} from '../components/admin/gameFields.js';
import { Focusable, FocusZone } from '../components/focus/Focusable.js';
import { FocusableTextField } from '../components/focus/OnScreenKeyboard.js';
import { EmptyState, Loading } from '../components/layout/Loading.js';
import { errorMessage } from '../lib/errors.js';
import { capabilities } from '../platform/index.js';

export const Route = createFileRoute('/admin/games/')({ component: AdminGames });

/**
 * The games list (`03-client-app.md` section 4.4).
 *
 * A row and nothing more. Editing a game opens `/admin/games/:id/edit`, because a game has a
 * description, six pieces of artwork and a gallery, and none of that fits inside a list item —
 * when it used to expand in place, opening one game pushed every other one off the screen.
 *
 * What stays here is what is true of a game at a glance and changes with one press: whether it
 * is published, and where its builds are.
 */
function AdminGames() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { isAdmin } = useAccess();
  const [creating, setCreating] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const games = useAdminGames();

  const refresh = () => void qc.invalidateQueries({ queryKey: keys.admin.games });

  const togglePublished = useMutation({
    mutationFn: (game: AdminGame) =>
      api.patch(`/admin/games/${game.id}`, { published: !game.published }),
    onSuccess: refresh,
    onError: (err) => setError(errorMessage(err)),
  });

  if (!isAdmin) return <EmptyState title={'You cannot do that.'} />;
  if (games.isLoading) return <Loading />;

  const rows = games.data ?? [];
  const openEditor = (game: AdminGame) =>
    void navigate({ to: '/admin/games/$id/edit', params: { id: game.id } });

  return (
    <div className="gb-stack">
      {error ? (
        <Callout.Root color="danger">
          <Callout.Description>{error}</Callout.Description>
        </Callout.Root>
      ) : null}

      <Focusable autoFocus onSelect={() => setCreating((c) => !c)} selectLabel={'New game'}>
        <Button size="2">{'New game'}</Button>
      </Focusable>

      {creating ? <NewGameForm onCancel={() => setCreating(false)} /> : null}

      <FocusZone focusKey="admin-games" direction="column" className="gb-stack">
        {rows.length === 0 ? (
          <EmptyState title={'No games here yet.'} />
        ) : (
          rows.map((game) => (
            <Card key={game.id} size="2" className="gb-gamerow">
              {/* The shelf tile at a glance. A game with no capsule shows the gap, which is
                  the fastest way to spot one that was created and never dressed. */}
              <div
                className="gb-gamerow__thumb"
                data-empty={game.capsuleImageUrl ? undefined : true}
              >
                {game.capsuleImageUrl ? <img src={game.capsuleImageUrl} alt="" /> : null}
              </div>

              <div className="gb-stack gb-stack--tight gb-gamerow__body">
                <div className="gb-row gb-row--tight">
                  <Heading size="3">{game.title}</Heading>
                  <GameKindTag kind={game.kind} />
                  {game.published ? (
                    <Badge color="green">{'Published'}</Badge>
                  ) : (
                    <Badge color="gray">{'Draft'}</Badge>
                  )}
                </div>
                <Text size="1" color="gray" mono>
                  {`/games/${game.slug}`}
                </Text>
                {game.shortDescription ? (
                  <Text size="1" color="gray">
                    {game.shortDescription}
                  </Text>
                ) : null}
              </div>

              <div className="gb-row gb-row--tight">
                <Focusable onSelect={() => openEditor(game)} selectLabel={'Edit'}>
                  <Button size="1" variant="surface">
                    {'Edit'}
                  </Button>
                </Focusable>
                <Focusable onSelect={() => togglePublished.mutate(game)} selectLabel={'Published'}>
                  <Button size="1" variant="ghost">
                    {game.published ? 'Unpublish' : 'Publish'}
                  </Button>
                </Focusable>
                {capabilities.uploadBuilds ? (
                  <Focusable
                    onSelect={() =>
                      void navigate({ to: '/admin/games/$id/builds', params: { id: game.id } })
                    }
                    selectLabel={'Builds'}
                  >
                    <Button size="1" variant="surface">
                      {'Builds'}
                    </Button>
                  </Focusable>
                ) : null}
              </div>
            </Card>
          ))
        )}
      </FocusZone>
    </div>
  );
}

/**
 * Everything a game needs on the day it is created.
 *
 * The old form asked for four things and left the rest to a second trip through Edit, which
 * meant a new game was always half filled in. The checks below are the server's own rules
 * restated, so a mistake is caught next to the field instead of coming back as a 400.
 *
 * Creating opens the editor on the new game rather than dropping back to the list: the art and
 * the builds are the next thing wanted every time, and neither of them is asked for here.
 */
function NewGameForm({ onCancel }: { onCancel: () => void }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [title, setTitle] = React.useState('');
  const [slug, setSlug] = React.useState('');
  // Until the admin types an address themselves, the title keeps writing it for them.
  const [slugTouched, setSlugTouched] = React.useState(false);
  const [kind, setKind] = React.useState<GameKind>('game');
  const [shortDescription, setShortDescription] = React.useState('');
  const [descriptionBb, setDescriptionBb] = React.useState('');
  const [androidPackageName, setAndroidPackageName] = React.useState('');
  const [storeUrls, setStoreUrls] = React.useState<StoreUrls>({ steam: '', epic: '' });
  const [sortOrder, setSortOrder] = React.useState('0');
  const [error, setError] = React.useState<string | null>(null);

  const effectiveSlug = (slugTouched ? slug : slugify(title)).trim();
  const android = androidPackageName.trim();

  const problems: string[] = [];
  if (!title.trim()) problems.push('A title.');
  if (!effectiveSlug) problems.push('An address.');
  else if (!SLUG_RULE.test(effectiveSlug)) {
    problems.push('The address takes lowercase letters, numbers and single hyphens.');
  }
  if (android && !ANDROID_PACKAGE_RULE.test(android)) {
    problems.push('The Android package name looks like com.studio.game.');
  }
  if (sortOrder.trim() && !Number.isInteger(Number(sortOrder))) {
    problems.push('The order is a whole number.');
  }
  problems.push(...storeProblems(storeUrls));

  const create = useMutation({
    mutationFn: () =>
      api.post<{ id: string; slug: string }>('/admin/games', {
        slug: effectiveSlug,
        title: title.trim(),
        kind,
        shortDescription: shortDescription.trim(),
        descriptionBb,
        androidPackageName: android || null,
        steamUrl: storeUrls.steam.trim() || null,
        epicUrl: storeUrls.epic.trim() || null,
        sortOrder: Number(sortOrder.trim() || '0'),
      }),
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: keys.admin.games });
      void navigate({ to: '/admin/games/$id/edit', params: { id: created.id } });
    },
    onError: (err) => setError(errorMessage(err)),
  });

  return (
    // `gb-form` spaces the fields further apart than a plain stack: a form is read one field at
    // a time, and the labels are small enough that a tight gap runs them together.
    <Card size="3" className="gb-form">
      <div className="gb-stack gb-stack--tight">
        <Heading size="4">{'New game'}</Heading>
        <Text size="2" color="gray">
          {'It is created as a draft. The editor opens on it, for the art and the builds.'}
        </Text>
      </div>

      <FocusableTextField
        value={title}
        onChange={setTitle}
        label={'Title'}
        maxLength={120}
        autoFocus
      />

      {/* The hint belongs to the field above it, so it sits closer to it than the next field
          does. */}
      <div className="gb-stack gb-stack--tight">
        <FocusableTextField
          value={effectiveSlug}
          onChange={(next) => {
            setSlugTouched(true);
            setSlug(next);
          }}
          label={'Address'}
          placeholder={'from-the-title'}
          maxLength={64}
        />
        <Text size="1" color="gray">
          {effectiveSlug
            ? `/games/${effectiveSlug} — fixed once the game is created.`
            : 'The address is written from the title.'}
        </Text>
      </div>

      <KindPicker value={kind} onChange={setKind} />

      <FocusableTextField
        value={shortDescription}
        onChange={setShortDescription}
        label={'Short description'}
        placeholder={'The one line on the tile.'}
        maxLength={300}
      />
      <SteamDescriptionField value={descriptionBb} onChange={setDescriptionBb} />
      <StoreUrlFields
        values={storeUrls}
        onChange={(store, next) => setStoreUrls((prev) => ({ ...prev, [store]: next }))}
      />
      <FocusableTextField
        value={androidPackageName}
        onChange={setAndroidPackageName}
        label={'Android package name'}
        placeholder={'com.studio.game'}
        maxLength={120}
      />
      <FocusableTextField
        value={sortOrder}
        onChange={setSortOrder}
        label={'Order on the shelf'}
        type="number"
      />

      {/* What is still missing, while it is still missing. The server says the same thing, but
          only after a round trip. */}
      {problems.length > 0 && (title.trim() || slug.trim()) ? (
        <Callout.Root color="amber">
          <Callout.Description>{problems.join(' ')}</Callout.Description>
        </Callout.Root>
      ) : null}

      {error ? (
        <Callout.Root color="danger">
          <Callout.Description>{error}</Callout.Description>
        </Callout.Root>
      ) : null}

      <div className="gb-row gb-form__actions">
        <Focusable
          onSelect={() => create.mutate()}
          selectLabel={'Create'}
          disabled={problems.length > 0 || create.isPending}
        >
          <Button size="2" disabled={problems.length > 0 || create.isPending}>
            {create.isPending ? 'Creating' : 'Create'}
          </Button>
        </Focusable>
        <Focusable onSelect={onCancel} selectLabel={'Cancel'}>
          <Button size="2" variant="ghost">
            {'Cancel'}
          </Button>
        </Focusable>
      </div>
    </Card>
  );
}
